<template>
    <div class="checkout__form">

        <!-- Skeleton init -->
        <template v-if="initLoading">
            <div class="checkout__skeleton">
                <div class="checkout__skeleton-line"></div>
                <div class="checkout__skeleton-line checkout__skeleton-line--short"></div>
            </div>
        </template>

        <!-- Trial guard: уже использовал -->
        <template v-else-if="trialUsed">
            <div class="sub-state">
                <div class="sub-state__icon">&#10003;</div>
                <div class="sub-state__title">Trial already used</div>
                <div class="sub-state__sub">
                    You've already used your free trial on this account. Choose a paid plan to continue.
                </div>
                <a :href="pricingUrl" class="btn btn--primary btn--lg sub-state__btn">View paid plans &rarr;</a>
            </div>
        </template>

        <!-- Уже подписан: управление в аккаунте -->
        <template v-else-if="redirectToAccount">
            <div class="sub-state">
                <div class="sub-state__icon">&#10003;</div>
                <div class="sub-state__title">You already have a subscription</div>
                <div class="sub-state__sub">
                    Manage your plan, upgrade, or view billing right from your account.
                </div>
                <a :href="accountUrl" class="btn btn--primary btn--lg sub-state__btn">Go to my account &rarr;</a>
            </div>
        </template>

        <!-- Форма -->
        <template v-else>

            <!-- Contact - только гостям: auth-юзеру email/телефон бэк
                 берёт из профиля, форма - бутафория. Вместо неё одна
                 строка "куда уедет подписка" - снимает тревогу у кнопки -->
            <template v-if="! isAuth">

                <h2 class="checkout__section-title">Contact</h2>

                <div class="checkout__field">
                    <label class="checkout__label" for="co-email">Email</label>
                    <input id="co-email"
                           v-model.trim="form.email"
                           @blur="checkEmail"
                           class="checkout__input"
                           :class="{ 'checkout__input--error': errors.email }"
                           type="email"
                           autocomplete="email"
                           placeholder="you@example.com"
                           required>
                    <p v-if="errors.email" class="checkout__field-error" role="alert">
                        Enter a valid email &mdash; we'll send your receipt and sign-in link there.
                    </p>
                    <p v-if="emailHasSub" class="checkout__field-error" role="alert">
                        An account with this email already has a subscription.
                        <a :href="accountUrl">Log in to manage it &rarr;</a>
                    </p>
                </div>

                <div class="checkout__field">
                    <label class="checkout__label" for="co-phone">Phone (optional)</label>
                    <input id="co-phone" v-model.trim="form.phone"
                           class="checkout__input" type="tel" autocomplete="tel">
                </div>

            </template>

            <p v-else-if="form.email" class="checkout__signed">
                Signed in as <b>{{ form.email }}</b>
            </p>

            <!-- Payment: динамический подкомпонент по gateway из init
                 (канон Checkout.vue eComzy) -->
            <component
                :is="paymentComponent"
                v-if="paymentComponent && publicKey"
                ref="payment"
                :public-key="publicKey"
                :saved-cards="savedCards"
                :amount="price"
                :slug="subscriptionSlug"
                :tier="tier"
                :contact="contactPayload"
                :is-auth="isAuth"
                :success-url="successUrl"
                @error="onPaymentError"
                @loading="onPaymentLoading"
                @payment-info="onPaymentInfo"
            ></component>

            <p v-if="globalError" class="checkout__error">{{ globalError }}</p>

            <button class="btn btn--primary btn--lg btn--block checkout__submit" type="button"
                    :disabled="isLoading || ! paymentComponent || emailHasSub"
                    @click="proceedToPayment">
                <span v-if="isLoading">Processing&hellip;</span>
                <span v-else>{{ ctaText }}</span>
            </button>

            <!-- Terms по плану (тексты макета; даты считает сервер) -->
            <p v-if="isTrial" class="checkout__terms">
                By starting your trial you agree to the <a :href="termsUrl">Terms&nbsp;&amp;&nbsp;Conditions</a>.
                From <b>{{ trialEnd }}</b> you'll be charged <b>{{ formatPrice }}/month</b> until you cancel.
            </p>
            <p v-else class="checkout__terms">
                By subscribing you agree to the <a :href="termsUrl">Terms&nbsp;&amp;&nbsp;Conditions</a>.
                <b>{{ formatPrice }}/month</b> until you cancel &mdash; anytime, in your account settings.
            </p>

            <div class="checkout__secure">
                <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg> Secured by Stripe</span>
                <span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M9 12l2 2 4-4"></path></svg> SSL 256-bit encryption</span>
            </div>

        </template>

    </div>
</template>

<script>
/**
 * SubscribeApp (m1) - порт eComzy SubscribeApp.vue.
 *
 * Дирижёр подписочного чекаута: init (trial-гард / redirect-to-account /
 * префилл / gateway-конфиг), checkEmail на blur (fail-open: intent отловит
 * дубль на бэке), submit делегируется PaymentStripeSubscribe через $refs.
 *
 * Отличия от донора:
 * - GTM снят (контура нет на m1);
 * - window.baseURL/accURL -> пропсы (канон data-атрибутов m1);
 * - разметка на checkout__* BEM макета m1 (у донора card/fg/fi);
 * - CTA/terms - тексты макета, даты (trialEnd) считает СЕРВЕР и отдаёт
 *   пропом - клиентского Date.now нет;
 * - accountUrl/pricingUrl - ЗАГЛУШКИ до ЛК и pricing-страницы.
 *
 * 27.07: Contact-секция только гостям - под auth вместо неё строка
 * "Signed in as {email}" (бэк для auth берёт email из $this->auth,
 * форма была бутафорией; contact с префиллом init уходит в intent
 * как и раньше - телефон/страна не теряются).
 */
import PaymentStripeSubscribe from './PaymentStripeSubscribe.vue';
import { track, dollars } from '../../js/utils/track';

export default {
    name: 'SubscribeApp',

    components: {
        PaymentStripeSubscribe,
    },

    props: {
        subscriptionSlug : { type: String,  required: true },
        tier             : { type: String,  required: true },   // 'trial' | 'monthly'
        planTitle        : { type: String,  default: '' },      // название плана (GTM item_name)
        price            : { type: Number,  required: true },   // центы
        trialEnd         : { type: String,  default: '' },      // 'F j, Y' с сервера
        isAuth           : { type: Boolean, default: false },
        userEmail        : { type: String,  default: '' },
        successUrl       : { type: String,  required: true },
        accountUrl       : { type: String,  default: '/account' },          // ЗАГЛУШКА до ЛК
        pricingUrl       : { type: String,  default: '/infinity-pass' },    // ЗАГЛУШКА до pricing
        termsUrl         : { type: String,  default: '/terms-and-conditions' },
    },

    data() {
        return {
            initLoading       : true,
            trialUsed         : false,
            redirectToAccount : false,
            emailChecking     : false,
            emailHasSub       : false,

            form : {
                first_name  : '',
                last_name   : '',
                email       : '',
                phone       : '',
                country     : 'US',
                state       : '',
                city        : '',
                address     : '',
                address2    : '',
                postal_code : '',
            },

            errors      : {},
            globalError : '',
            isLoading   : false,

            // Из init response
            publicKey  : '',
            savedCards : [],
            gateway    : '',
        };
    },

    computed: {

        // Динамический подкомпонент по типу gateway из init (канон донора)
        paymentComponent() {

            if( this.gateway === 'stripe' )
                return 'PaymentStripeSubscribe';

            // future: 'PaymentPayPalSubscribe' и т.д.

            return null;
        },

        isTrial() {
            return this.tier === 'trial';
        },

        // Для auth бэк возьмёт email из сессии, contact уходит как есть
        contactPayload() {
            return this.form;
        },

        formatPrice() {
            return '$' + ( this.price / 100 ).toFixed(2);
        },

        ctaText() {

            if( this.isTrial )
                return 'Start my free trial — $0.00 today';

            return 'Subscribe — ' + this.formatPrice + '/month';
        },
    },

    mounted() {

        this.form.email = this.userEmail || '';

        this.init();
        window.addEventListener( 'pageshow', this.resetLoader );
    },

    beforeUnmount() {
        window.removeEventListener( 'pageshow', this.resetLoader );
    },

    methods: {

        // bfcache-возврат со Stripe: снять спиннер (донор дословно)
        resetLoader( event ) {
            if( event.persisted )
                this.isLoading = false;
        },

        async init() {

            try {

                const response = await this.$http.post( '/subscribe/init', {
                    slug : this.subscriptionSlug,
                } );
                const { status, data } = response?.data || {};

                if( status !== 'success' )
                    throw new Error( data?.message || 'Init failed' );

                // Trial-гард: auth-юзер уже использовал trial
                this.trialUsed = data.trial_used || false;

                if( this.trialUsed )
                    return;

                // Уже подписан - управление в аккаунте
                this.redirectToAccount = data.redirect_to_account || false;

                if( this.redirectToAccount )
                    return;

                // Префилл (для Stripe Element)
                if( data.user ) {
                    this.form.email       = data.user.email       || this.form.email;
                    this.form.country     = data.user.country     || 'US';
                    this.form.postal_code = data.user.postal_code || '';
                    this.form.phone       = data.user.phone       || '';
                }

                this.publicKey  = data.stripe_pub_key || '';
                this.savedCards = data.saved_cards || [];
                this.gateway    = data.gateway || '';

                // Форма оплаты открыта: цена и план известны (trial_used/redirect выше - формы нет, события нет)
                track( 'begin_checkout', {}, this.trackingEcommerce() );

            } catch( e ) {
                this.globalError = 'Failed to load checkout. Please refresh the page.';
            } finally {
                this.initLoading = false;
            }
        },

        // Fail-open: при ошибке проверки не блокируем - intent отловит дубль
        async checkEmail() {

            const email = this.form.email?.trim();

            if( this.isAuth || ! email || ! /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( email ) ) {
                this.emailHasSub = false;
                return;
            }

            this.emailChecking = true;

            try {

                const response = await this.$http.post( '/subscribe/checkEmail', { email } );
                const { status, data } = response?.data || {};

                if( status === 'success' )
                    this.emailHasSub = data.has_subscription || false;

            } catch( e ) {
                this.emailHasSub = false;
            } finally {
                this.emailChecking = false;
            }
        },

        proceedToPayment() {

            if( this.emailHasSub ) {
                this.globalError = 'Please log in to manage your existing subscription.';
                return;
            }

            if( ! this.isAuth && ! this.validate() )
                return;

            this.globalError = '';

            if( this.$refs.payment?.submit )
                this.$refs.payment.submit();
        },

        validate() {

            const e = {};

            if( ! this.form.email || ! /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test( this.form.email ) )
                e.email = true;

            this.errors = e;

            return Object.keys( e ).length === 0;
        },

        // GTM ecommerce-объект: деньги в долларах (в коде центы). Триал - value 0:
        // списания сегодня нет (SetupIntent), items.price - цена плана
        trackingEcommerce() {

            return {
                currency : 'USD',
                value    : this.isTrial ? 0 : dollars( this.price ),
                items    : [{
                    item_id      : this.subscriptionSlug,
                    item_name    : this.planTitle || this.subscriptionSlug,
                    item_variant : this.tier,
                    price        : dollars( this.price ),
                    quantity     : 1,
                }],
            };
        },

        // Форма оплаты прошла валидацию, карта уходит на подтверждение
        onPaymentInfo() {
            track( 'add_payment_info', {}, this.trackingEcommerce() );
        },

        onPaymentError( message ) {
            this.globalError = message || 'Payment failed';
            this.isLoading   = false;
        },

        onPaymentLoading( isLoading ) {
            this.isLoading = isLoading;
        },
    },
};
</script>